import {
  faBinoculars,
  faBullhorn,
  faChartNetwork,
  faLayerGroupPlus,
  faLayerPlus
} from "@fortawesome/pro-regular-svg-icons"
import { faChartBar, faChartSimple, faFolderOpen, faGears, faPersonSign } from "@fortawesome/pro-solid-svg-icons"
import {
  ADVERTISERS_PATH,
  CAMPAIGN_CREATE_PATH,
  DSP_DEAL_CREATE_PATH,
  FORECAST_CREATE_PATH,
  HOME_PATH,
  MY_CAMPAIGNS_PATH,
  MY_FORECASTS_PATH,
  MY_SSP_DEALS_PATH,
  ORDERS_PATH,
  REPORTS_CREATE_PATH,
  REPORTS_MY_PATH,
  REVENUE_PATH,
  SETTINGS_PATH,
  SSP_MEDIA_APPROVALS_PATH
} from "page/model"
import { getDefaultUIConfiguration } from "ui/configuration/tools/state"
import { getDefaultTheme } from "ui/tools/theme"
import { IUserInfo } from "user/model"
import { getEmptyUserInfo } from "user/store/state"
import {
  canAccessDspDeal,
  canAccessForecast,
  canAccessReports,
  canReadCampaigns,
  canSaveCampaigns
} from "user/tools/permissions"
import {
  IMenu,
  IMenuItem,
  IUiState,
  MAX_PHONE_WIDTH_IN_PX,
  MAX_TABLET_WIDTH_IN_PX,
  MENU_CLOSED_WIDTH_IN_PX,
  MENU_OPENED_WIDTH_IN_PX,
  SCREEN_TYPES
} from "../model"

export function getDefaultUiState(): IUiState {
  const isMenuOpened = document.body.offsetWidth > MAX_TABLET_WIDTH_IN_PX
  const screenType = getScreenType()
  return {
    theme: getDefaultTheme(),
    config: getDefaultUIConfiguration(),
    tempConfig: getDefaultUIConfiguration(),
    uiConfigUndoRedoHistory: [getDefaultUIConfiguration()],
    isDarkPreview: false,
    uiConfigUndoRedoIndex: 0,
    isSavingConfig: false,
    isMenuOpened,
    activeMenuPath: CAMPAIGN_CREATE_PATH,
    menus: getNonAdminMenus(getEmptyUserInfo()),
    screenType,
    contentWidth: getContentWidth(screenType, isMenuOpened),
    container: {
      width: document.body.offsetWidth,
      height: document.body.offsetHeight
    },
    colorPickerPopup: {
      title: "",
      isOpened: false,
      hexColor: "#ffffff",
      setHexColor: () => undefined
    }
  }
}

export const getNonAdminMenus = (userInfo: IUserInfo): IMenuItem[] => {
  const menus: IMenuItem[] = []
  /* {
    titleKey: "page.dashboard.title",
    pagePaths: [DASHBOARD_PATH],
    icon: regular("chart-tree-map"),
    items: []
  }*/
  const canReadDspDeals = canAccessDspDeal(userInfo)
  const readCampaigns = canReadCampaigns(userInfo)
  if (canReadDspDeals) {
    menus.push({
      titleKey: "revenue.title",
      pagePaths: [],
      items: [
        {
          titleKey: "revenue.subtitle",
          icon: faChartSimple,
          pagePaths: [REVENUE_PATH, HOME_PATH]
        }
      ]
    })
    const dspDealMenu: IMenuItem = {
      titleKey: "dsp-deal.title",
      pagePaths: [],
      items: [
        {
          titleKey: "dsp-deal.create.title",
          icon: faLayerPlus,
          pagePaths: [DSP_DEAL_CREATE_PATH]
        },
        {
          titleKey: "dsp-deal.my.title",
          icon: faBullhorn,
          pagePaths: [MY_SSP_DEALS_PATH]
        }
      ]
    }
    if (!readCampaigns) {
      dspDealMenu.items.push(
        ...[
          {
            titleKey: "order.my.title",
            icon: faFolderOpen,
            pagePaths: [ORDERS_PATH]
          },
          {
            titleKey: "advertiser.my.title",
            icon: faPersonSign,
            pagePaths: [ADVERTISERS_PATH]
          }
        ]
      )
    }
    menus.push(dspDealMenu)
    menus.push({
      titleKey: "media-approval.title",
      pagePaths: [],
      items: [
        {
          titleKey: "media-approval.list.title",
          icon: faBinoculars,
          pagePaths: [SSP_MEDIA_APPROVALS_PATH]
        }
      ]
    })
  }
  if (readCampaigns) {
    const saveCampaigns = canSaveCampaigns(userInfo)
    const campaignMenu: IMenuItem = {
      titleKey: "campaign.title",
      pagePaths: [],
      items: []
    }
    if (saveCampaigns) {
      campaignMenu.items.push({
        titleKey: "campaign.create.title",
        icon: faLayerPlus,
        pagePaths: [CAMPAIGN_CREATE_PATH]
      })
    }
    campaignMenu.items.push(
      ...[
        {
          titleKey: "campaign.my.title",
          icon: faBullhorn,
          pagePaths: canReadDspDeals ? [MY_CAMPAIGNS_PATH] : [MY_CAMPAIGNS_PATH, HOME_PATH]
        },
        {
          titleKey: "order.my.title",
          icon: faFolderOpen,
          pagePaths: [ORDERS_PATH]
        },
        {
          titleKey: "advertiser.my.title",
          icon: faPersonSign,
          pagePaths: [ADVERTISERS_PATH]
        }
      ]
    )
    menus.push(campaignMenu)
  }
  if (canAccessForecast(userInfo)) {
    menus.push({
      titleKey: "planning.title",
      pagePaths: [],
      items: [
        {
          titleKey: "forecast.create.title",
          icon: faLayerGroupPlus,
          pagePaths: [FORECAST_CREATE_PATH]
        },
        {
          titleKey: "forecast.my.title",
          icon: faBinoculars,
          pagePaths: [MY_FORECASTS_PATH]
        }
      ]
    })
  }
  if (canAccessReports(userInfo)) {
    menus.push({
      titleKey: "reports.title",
      pagePaths: [],
      items: [
        {
          titleKey: "reports.create.title",
          icon: faChartNetwork,
          pagePaths: [REPORTS_CREATE_PATH]
        },
        {
          titleKey: "reports.my.title",
          icon: faChartBar,
          pagePaths: [REPORTS_MY_PATH]
        }
      ]
    })
  }
  return menus
}

export const getSettingsMenu = (): IMenu => ({
  icon: faGears,
  pagePaths: [SETTINGS_PATH],
  titleKey: "settings.page.title"
})

export function getScreenType() {
  const widthInPx = document.body.offsetWidth
  if (widthInPx < MAX_PHONE_WIDTH_IN_PX) {
    return SCREEN_TYPES.PHONE
  } else if (widthInPx < MAX_TABLET_WIDTH_IN_PX) {
    return SCREEN_TYPES.TABLET
  } else {
    return SCREEN_TYPES.DESKTOP
  }
}

export function getContentWidth(screenType: SCREEN_TYPES, isMenuOpened: boolean): number {
  if (screenType !== SCREEN_TYPES.DESKTOP) {
    return document.body.offsetWidth
  }
  return document.body.offsetWidth - (isMenuOpened ? MENU_OPENED_WIDTH_IN_PX : MENU_CLOSED_WIDTH_IN_PX)
}
